import { useTranslation } from 'react-i18next';
import { ArchitectureDiagram } from '../components/how-built/ArchitectureDiagram';
import { RepoTree } from '../components/how-built/RepoTree';
import { GitFlowDiagram } from '../components/how-built/GitFlowDiagram';
import { CicdPipeline } from '../components/how-built/CicdPipeline';
import { TechBadges } from '../components/how-built/TechBadges';
import { DddDiagram } from '../components/how-built/DddDiagram';
import { DecisionCard } from '../components/how-built/DecisionCard';
import { CodeBlock } from '../components/how-built/CodeBlock';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-mono text-sm text-accent">{`// ${title}`}</h2>
      <div className="mt-6 space-y-6">{children}</div>
    </section>
  );
}

export function HowIWasBuilt() {
  const { i18n } = useTranslation();
  const en = i18n.language === 'en';
  const lang = en ? 'en' : 'es';

  return (
    <main className="mx-auto max-w-5xl px-6 pt-24 pb-16">
      <header className="mb-16">
        <h1 className="text-3xl font-bold md:text-4xl">
          {en ? 'How I Was Built' : 'Cómo me construyeron'}
        </h1>
        <p className="mt-4 max-w-3xl text-text-muted leading-relaxed">
          {en
            ? 'This isn\'t just a portfolio — it\'s a living technical document. Every architecture decision, every tradeoff, every line of infrastructure was discussed, evaluated, and documented before writing code. Built entirely with AI-assisted development using Claude Code as the orchestrator.'
            : 'Esto no es solo un portfolio — es un documento técnico vivo. Cada decisión de arquitectura, cada tradeoff, cada línea de infraestructura fue discutida, evaluada y documentada antes de escribir código. Construido íntegramente con desarrollo asistido por IA usando Claude Code como orquestador.'}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
            {en ? 'AI-Assisted Development' : 'Desarrollo Asistido por IA'}
          </span>
          <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
            Spec-Driven Development
          </span>
          <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
            100% Serverless
          </span>
          <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
            {en ? 'Infrastructure as Code' : 'Infraestructura como Código'}
          </span>
        </div>
      </header>

      <div className="space-y-20">
        {/* TECH STACK */}
        <Section id="stack" title={en ? 'Tech Stack' : 'Tech Stack'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'The entire project runs on AWS with a fully serverless architecture. No servers to manage, no scaling to configure. Pay only for what you use.'
              : 'El proyecto completo corre en AWS con una arquitectura 100% serverless. Sin servidores que administrar, sin escalado que configurar. Pagás solo por lo que usás.'}
          </p>
          <TechBadges />
        </Section>

        {/* ARCHITECTURE */}
        <Section id="architecture" title={en ? 'Architecture' : 'Arquitectura'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'A CDK monorepo with nested stacks, one per domain. The frontend is a React SPA served through CloudFront. Two independent API Gateways handle Portfolio and AI Chat domains, each with their own DynamoDB tables and Lambda functions. Zero shared state between domains.'
              : 'Un monorepo CDK con nested stacks, una por dominio. El frontend es una SPA React servida a través de CloudFront. Dos API Gateways independientes manejan los dominios Portfolio y AI Chat, cada uno con sus propias tablas DynamoDB y funciones Lambda. Cero estado compartido entre dominios.'}
          </p>
          <div className="rounded border border-yellow-500/30 bg-yellow-500/5 px-5 py-4">
            <p className="font-mono text-xs text-yellow-400">
              {en ? '// Why a backend for a portfolio?' : '// ¿Por qué un backend para un portfolio?'}
            </p>
            <p className="mt-2 text-sm text-text-muted leading-relaxed">
              {en
                ? 'Honestly? This website doesn\'t need a backend. The portfolio data could live entirely in the frontend. But the goal here is to showcase HOW you work with a real backend: DDD bounded contexts, Lambda handlers with TDD, DynamoDB data modeling, API Gateway configuration, and CI/CD that ties it all together. The backend exists as a technical demonstration — so anyone reviewing this project can see the full stack in action.'
                : 'Siendo honesto, esta web no necesita un backend. Los datos del portfolio podrían vivir enteramente en el frontend. Pero el objetivo acá es mostrar CÓMO se trabaja con un backend real: bounded contexts con DDD, Lambda handlers con TDD, modelado de datos en DynamoDB, configuración de API Gateway, y CI/CD que conecta todo. El backend existe como demostración técnica — para que quien revise este proyecto pueda ver el stack completo en acción.'}
            </p>
          </div>
          <ArchitectureDiagram lang={lang} />
        </Section>

        {/* REPOSITORY STRUCTURE */}
        <Section id="repo" title={en ? 'Repository Structure' : 'Estructura del Repositorio'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'NPM workspaces monorepo with strict separation: infrastructure (CDK), application logic (backend), and user interface (frontend). Each workspace has its own package.json, tsconfig, and test configuration. The key principle: IaC and business logic NEVER mix — CDK stacks reference handler paths but never contain application code.'
              : 'Monorepo con NPM workspaces y separación estricta: infraestructura (CDK), lógica de aplicación (backend) e interfaz de usuario (frontend). Cada workspace tiene su propio package.json, tsconfig y configuración de tests. El principio clave: IaC y lógica de negocio NUNCA se mezclan — los stacks CDK referencian paths de handlers pero nunca contienen código de aplicación.'}
          </p>
          <RepoTree />
        </Section>

        {/* DDD */}
        <Section id="ddd" title={en ? 'Domain-Driven Design' : 'Domain-Driven Design'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'Two bounded contexts with complete independence. Each domain owns its infrastructure, API, database, and business logic. You could extract either domain into its own repository without changing a single line of code in the other.'
              : 'Dos bounded contexts con independencia total. Cada dominio es dueño de su infraestructura, API, base de datos y lógica de negocio. Podrías extraer cualquier dominio a su propio repositorio sin cambiar una sola línea de código del otro.'}
          </p>
          <DddDiagram lang={lang} />
          <div>
            <h3 className="mb-3 font-mono text-xs text-text-primary">
              {en ? 'DynamoDB Data Model — Single Table per Domain' : 'Modelo de Datos DynamoDB — Single Table por Dominio'}
            </h3>
            <CodeBlock title="portfolio-table">
              {`PK              SK              entityType    order
─────────────── ─────────────── ───────────── ─────
PROFILE         INFO            -             -
EXPERIENCE      ITEM#001        EXPERIENCE    1
EXPERIENCE      ITEM#002        EXPERIENCE    2
CERTIFICATION   ITEM#001        CERTIFICATION 1
SKILL           ITEM#001        SKILL         1
PROJECT         ITEM#001        PROJECT       1
CONTACT         ITEM#<uuid>     CONTACT       -

GSI: type-order-index (entityType → order) ${en ? 'for sorted queries' : 'para queries ordenados'}`}
            </CodeBlock>
          </div>
          <div>
            <CodeBlock title="ai-chat-table">
              {`PK              SK                  TTL
─────────────── ─────────────────── ──────────────
SESSION#<uuid>  META                expiresAt
SESSION#<uuid>  MSG#<timestamp>     expiresAt

${en ? 'Sessions auto-expire via DynamoDB TTL' : 'Las sesiones expiran automáticamente via DynamoDB TTL'}`}
            </CodeBlock>
          </div>
        </Section>

        {/* TDD */}
        <Section id="tdd" title={en ? 'Test-Driven Development' : 'Test-Driven Development'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'Every feature was built test-first. CDK stacks are validated with aws-cdk-lib/assertions before deployment. Backend handlers and services have unit tests with mocked dependencies. The test pyramid: CDK assertions for infrastructure, unit tests for business logic, integration via real deployments.'
              : 'Cada feature se construyó test-first. Los stacks CDK se validan con aws-cdk-lib/assertions antes del deploy. Los handlers y servicios del backend tienen tests unitarios con dependencias mockeadas. La pirámide de tests: CDK assertions para infraestructura, tests unitarios para lógica de negocio, integración via deploys reales.'}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded border border-border bg-bg-surface p-4 text-center">
              <p className="font-mono text-2xl font-bold text-accent">4</p>
              <p className="mt-1 font-mono text-xs text-text-muted">{en ? 'CDK Test Suites' : 'Suites de Tests CDK'}</p>
              <p className="mt-2 text-xs text-text-muted">root, frontend, portfolio, ai-chat</p>
            </div>
            <div className="rounded border border-border bg-bg-surface p-4 text-center">
              <p className="font-mono text-2xl font-bold text-accent">12</p>
              <p className="mt-1 font-mono text-xs text-text-muted">{en ? 'Infra Tests' : 'Tests de Infra'}</p>
              <p className="mt-2 text-xs text-text-muted">{en ? 'Resource counts, configs, IAM' : 'Conteo de recursos, configs, IAM'}</p>
            </div>
            <div className="rounded border border-border bg-bg-surface p-4 text-center">
              <p className="font-mono text-2xl font-bold text-accent">8</p>
              <p className="mt-1 font-mono text-xs text-text-muted">{en ? 'Backend Test Files' : 'Archivos de Tests Backend'}</p>
              <p className="mt-2 text-xs text-text-muted">{en ? 'Handlers + Services' : 'Handlers + Services'}</p>
            </div>
          </div>
          <CodeBlock title={en ? 'Example: CDK TDD — Portfolio Stack' : 'Ejemplo: CDK TDD — Portfolio Stack'}>
            {`// ${en ? 'Write the test FIRST' : 'Escribir el test PRIMERO'}
it('creates DynamoDB table with GSI', () => {
  template.hasResourceProperties('AWS::DynamoDB::Table', {
    TableName: 'personalweb-portfolio-dev',
    KeySchema: [
      { AttributeName: 'PK', KeyType: 'HASH' },
      { AttributeName: 'SK', KeyType: 'RANGE' },
    ],
    GlobalSecondaryIndexes: Match.arrayWith([
      Match.objectLike({
        IndexName: 'type-order-index',
      }),
    ]),
  });
});

// ${en ? 'Then write the implementation to make it pass' : 'Luego escribir la implementación para que pase'}`}
          </CodeBlock>
        </Section>

        {/* GIT FLOW */}
        <Section id="gitflow" title={en ? 'Git Flow' : 'Git Flow'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'Feature branches for isolation. PRs to dev trigger automatic deployment. PRs to main require review. Production deploys are tag-triggered (prd-x.x.x). Two AWS accounts provide environment-level isolation.'
              : 'Feature branches para aislamiento. PRs a dev disparan deploy automático. PRs a main requieren review. Los deploys a producción se disparan por tag (prd-x.x.x). Dos cuentas AWS proveen aislamiento a nivel de ambiente.'}
          </p>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'For critical bugs in production, the hotfix flow branches directly from main (which mirrors what\'s live), applies the fix, and triggers a quick release via tag. Once production is stable, the hotfix is merged back into dev to keep both branches in sync — no fix gets lost, no untested code leaks into production.'
              : 'Para bugs críticos en producción, el flujo de hotfix sale directamente de main (que refleja lo que está en vivo), aplica la corrección y dispara un release rápido via tag. Una vez que producción está estable, el hotfix se mergea de vuelta a dev para mantener ambas ramas sincronizadas — ningún fix se pierde, ningún código sin testear se filtra a producción.'}
          </p>
          <GitFlowDiagram lang={lang} />
        </Section>

        {/* CI/CD */}
        <Section id="cicd" title={en ? 'CI/CD Pipeline' : 'Pipeline CI/CD'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'GitHub Actions with OIDC authentication — no static credentials. The key insight: the frontend is built AFTER CDK deploys the infrastructure, so API Gateway URLs can be injected as build-time environment variables. This eliminates hardcoded URLs and makes the pipeline truly environment-agnostic.'
              : 'GitHub Actions con autenticación OIDC — sin credenciales estáticas. El insight clave: el frontend se compila DESPUÉS de que CDK despliega la infraestructura, para poder inyectar las URLs de API Gateway como variables de entorno en build time. Esto elimina URLs hardcodeadas y hace que el pipeline sea verdaderamente agnóstico al ambiente.'}
          </p>
          <CicdPipeline lang={lang} />
          <div>
            <h3 className="mb-3 font-mono text-xs text-text-primary">
              {en ? 'OIDC Authentication Flow' : 'Flujo de Autenticación OIDC'}
            </h3>
            <CodeBlock title=".github/workflows/deploy-dev.yml">
              {`# ${en ? 'No static AWS credentials — GitHub OIDC assumes an IAM role' : 'Sin credenciales estáticas — GitHub OIDC asume un rol IAM'}
permissions:
  id-token: write    # ${en ? 'Required for OIDC' : 'Requerido para OIDC'}
  contents: read

- uses: aws-actions/configure-aws-credentials@v4
  with:
    role-to-assume: \${{ secrets.AWS_DEV_ROLE_ARN }}
    aws-region: \${{ secrets.AWS_REGION }}

# ${en ? 'Key: build frontend AFTER CDK deploy' : 'Clave: compilar frontend DESPUÉS del CDK deploy'}
- name: CDK Deploy          # ${en ? 'Step 7: infra first' : 'Paso 7: infra primero'}
- name: Get stack outputs   # ${en ? 'Step 8: extract API URLs' : 'Paso 8: extraer URLs de API'}
- name: Build frontend      # ${en ? 'Step 9: inject URLs' : 'Paso 9: inyectar URLs'}
  env:
    VITE_PORTFOLIO_API_URL: \${{ steps.outputs.outputs.portfolio_api_url }}
    VITE_CHAT_API_URL: \${{ steps.outputs.outputs.ai_chat_api_url }}`}
            </CodeBlock>
          </div>
        </Section>

        {/* AI INTEGRATION */}
        <Section id="ai" title={en ? 'AI Integration' : 'Integración IA'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'The AI chatbot uses Claude Haiku on AWS Bedrock — chosen for cost-effectiveness (~$0.25/1M input tokens) since portfolio Q&A responses are short (~300 tokens). The system prompt is hardened to only discuss Damián\'s career and reject off-topic questions.'
              : 'El chatbot de IA usa Claude Haiku en AWS Bedrock — elegido por costo-efectividad (~$0.25/1M tokens de entrada) ya que las respuestas de Q&A del portfolio son cortas (~300 tokens). El system prompt está blindado para solo hablar de la carrera de Damián y rechazar preguntas fuera de tema.'}
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded border border-border bg-bg-surface p-5">
              <h4 className="font-mono text-xs text-accent">{en ? 'Security Layers' : 'Capas de Seguridad'}</h4>
              <ul className="mt-3 space-y-2 text-sm text-text-muted">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Hardened system prompt — only answers about Damián' : 'System prompt blindado — solo responde sobre Damián'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Per-session rate limiting (messages + tokens)' : 'Rate limiting por sesión (mensajes + tokens)'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'API Key + Usage Plan on API Gateway' : 'API Key + Usage Plan en API Gateway'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'CloudWatch alarm → SNS on error spike' : 'CloudWatch alarm → SNS ante picos de errores'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'DynamoDB TTL auto-expires sessions' : 'DynamoDB TTL auto-expira sesiones'}</span>
                </li>
              </ul>
            </div>
            <div className="rounded border border-border bg-bg-surface p-5">
              <h4 className="font-mono text-xs text-accent">{en ? 'Why Haiku on Bedrock?' : '¿Por qué Haiku en Bedrock?'}</h4>
              <ul className="mt-3 space-y-2 text-sm text-text-muted">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Cost: ~$0.25/$1.25 per 1M tokens (vs Sonnet $3/$15)' : 'Costo: ~$0.25/$1.25 por 1M tokens (vs Sonnet $3/$15)'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Speed: fastest response for short Q&A' : 'Velocidad: respuesta más rápida para Q&A corto'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Bedrock: no API keys to manage, IAM-based auth' : 'Bedrock: sin API keys que gestionar, auth basado en IAM'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-accent">→</span>
                  <span>{en ? 'Data stays in AWS — no external API calls' : 'Los datos quedan en AWS — sin llamadas a APIs externas'}</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* METHODOLOGY */}
        <Section id="methodology" title={en ? 'Methodology — SDD (Tony & Jarvis)' : 'Metodología — SDD (Tony & Jarvis)'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'This project was built using SDD (Spec-Driven Development) — a methodology where a human architect and an AI assistant collaborate like Tony Stark and Jarvis. The human provides vision, makes architectural decisions, and validates quality. The AI accelerates execution: it reads the codebase, proposes solutions with tradeoffs, writes implementation plans, generates code following TDD, and runs tests.'
              : 'Este proyecto fue construido con SDD (Spec-Driven Development) — una metodología donde un arquitecto humano y un asistente de IA colaboran como Tony Stark y Jarvis. El humano provee la visión, toma decisiones arquitectónicas y valida la calidad. La IA acelera la ejecución: lee el codebase, propone soluciones con tradeoffs, escribe planes de implementación, genera código siguiendo TDD, y ejecuta tests.'}
          </p>
          <div className="rounded border border-accent/30 bg-accent/5 px-5 py-4">
            <p className="font-mono text-xs text-accent">
              {en ? '// The Tony & Jarvis dynamic' : '// La dinámica Tony & Jarvis'}
            </p>
            <p className="mt-2 text-sm text-text-muted leading-relaxed">
              {en
                ? '"Sir, I\'ve analyzed 3 approaches for the nested stack architecture. Option B gives you domain isolation with independent deployability, but adds operational overhead. Shall I proceed with the implementation plan?" — That\'s how every feature in this project was born. Not "generate me a website", but a structured conversation where every decision has a WHY.'
                : '"Señor, analicé 3 enfoques para la arquitectura de nested stacks. La opción B te da aislamiento de dominio con deployability independiente, pero agrega overhead operacional. ¿Procedo con el plan de implementación?" — Así nació cada feature de este proyecto. No "generame un sitio web", sino una conversación estructurada donde cada decisión tiene un POR QUÉ.'}
            </p>
            <p className="mt-3 text-sm text-text-muted leading-relaxed">
              {en
                ? 'The SDD orchestrator used in this project was built by '
                : 'El orquestador SDD usado en este proyecto fue construido por '}
              <a
                href="https://github.com/Gentleman-Programming/gentle-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-accent underline decoration-accent/30 underline-offset-2 transition-colors hover:decoration-accent"
              >
                Gentleman Programming
              </a>
              {en
                ? ' — an open-source framework for structured AI-assisted development with Claude Code.'
                : ' — un framework open-source para desarrollo estructurado asistido por IA con Claude Code.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              { step: '1', label: 'Brainstorm', desc: en ? 'Explore ideas' : 'Explorar ideas' },
              { step: '2', label: 'Design Spec', desc: en ? 'Document decisions' : 'Documentar decisiones' },
              { step: '3', label: en ? 'Task Breakdown' : 'Desglose de Tareas', desc: en ? '10 implementation tasks' : '10 tareas de implementación' },
              { step: '4', label: en ? 'TDD Execution' : 'Ejecución TDD', desc: en ? 'Test first, code second' : 'Test primero, código después' },
              { step: '5', label: 'Deploy', desc: en ? 'CI/CD pipeline' : 'Pipeline CI/CD' },
            ].map(({ step, label, desc }, i, arr) => (
              <div key={step} className="flex items-center gap-3">
                <div className="flex flex-col items-center rounded border border-border bg-bg-surface px-4 py-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/20 font-mono text-xs font-bold text-accent">
                    {step}
                  </span>
                  <span className="mt-2 font-mono text-xs text-text-primary">{label}</span>
                  <span className="mt-1 text-xs text-text-muted">{desc}</span>
                </div>
                {i < arr.length - 1 && (
                  <span className="hidden text-text-muted md:inline">→</span>
                )}
              </div>
            ))}
          </div>
          <div className="rounded border border-border bg-bg-surface p-5">
            <h4 className="font-mono text-xs text-accent">{en ? 'AI-Assisted Development — How It Actually Works' : 'Desarrollo Asistido por IA — Cómo Funciona Realmente'}</h4>
            <p className="mt-3 text-sm text-text-muted leading-relaxed">
              {en
                ? 'Claude Code acts as the orchestrator: it reads the codebase, proposes architecture decisions, writes implementation plans, generates code following TDD, and runs tests — all in conversation with the developer. The human provides direction, validates decisions, and catches edge cases. AI accelerates execution; the human ensures quality and coherence.'
                : 'Claude Code actúa como orquestador: lee el codebase, propone decisiones de arquitectura, escribe planes de implementación, genera código siguiendo TDD, y ejecuta tests — todo en conversación con el desarrollador. El humano provee la dirección, valida decisiones, y detecta edge cases. La IA acelera la ejecución; el humano asegura calidad y coherencia.'}
            </p>
          </div>

        </Section>

        {/* KEY DECISIONS */}
        <Section id="decisions" title={en ? 'Key Decisions' : 'Decisiones Clave'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'Real architectural decisions from the conversations that built this project. Each one was evaluated with explicit tradeoffs.'
              : 'Decisiones arquitectónicas reales de las conversaciones que construyeron este proyecto. Cada una fue evaluada con tradeoffs explícitos.'}
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <DecisionCard
              title={en ? 'Nested Stacks per Domain' : 'Nested Stacks por Dominio'}
              context={en ? 'How to organize CDK infrastructure for two DDD domains' : 'Cómo organizar la infraestructura CDK para dos dominios DDD'}
              decision={en ? 'One nested stack per bounded context, each with its own API Gateway and DynamoDB' : 'Un nested stack por bounded context, cada uno con su propio API Gateway y DynamoDB'}
              tradeoff={en ? 'More stacks to manage, but zero coupling. Any domain can be extracted to its own repo.' : 'Más stacks para gestionar, pero cero acoplamiento. Cualquier dominio se puede extraer a su propio repo.'}
            />
            <DecisionCard
              title={en ? 'Haiku over Sonnet' : 'Haiku sobre Sonnet'}
              context={en ? 'Which Claude model for the AI chatbot' : 'Qué modelo de Claude para el chatbot IA'}
              decision={en ? 'Claude Haiku on Bedrock for cost and speed' : 'Claude Haiku en Bedrock por costo y velocidad'}
              tradeoff={en ? 'Less capable for complex reasoning, but portfolio Q&A needs speed, not depth. 12x cheaper than Sonnet.' : 'Menos capaz para razonamiento complejo, pero el Q&A de portfolio necesita velocidad, no profundidad. 12x más barato que Sonnet.'}
            />
            <DecisionCard
              title={en ? 'No Shared API Gateway' : 'Sin API Gateway Compartido'}
              context={en ? 'One API Gateway for all domains or one per domain' : 'Un API Gateway para todos los dominios o uno por dominio'}
              decision={en ? 'Each domain gets its own API Gateway' : 'Cada dominio tiene su propio API Gateway'}
              tradeoff={en ? 'Slightly higher cost (two APIs), but complete independence. AI Chat has API keys + usage plans that would pollute Portfolio API.' : 'Costo ligeramente mayor (dos APIs), pero independencia total. AI Chat tiene API keys + usage plans que contaminarían la API de Portfolio.'}
            />
            <DecisionCard
              title={en ? 'CloudFront Default URL' : 'URL por Defecto de CloudFront'}
              context={en ? 'Custom domain or CloudFront distribution URL' : 'Dominio personalizado o URL de distribución CloudFront'}
              decision={en ? 'Start with CloudFront default, add custom domain later via CoreStack' : 'Empezar con default de CloudFront, agregar dominio custom después via CoreStack'}
              tradeoff={en ? 'Less professional URL initially, but no blocking on domain purchase. CoreStack is ready for ACM + Route53.' : 'URL menos profesional inicialmente, pero sin bloqueo por compra de dominio. CoreStack está listo para ACM + Route53.'}
            />
            <DecisionCard
              title={en ? 'IaC ≠ Business Logic' : 'IaC ≠ Lógica de Negocio'}
              context={en ? 'Where to put Lambda handler code — inside CDK stacks or separate workspace' : 'Dónde poner el código de handlers Lambda — dentro de stacks CDK o workspace separado'}
              decision={en ? 'Strict separation: backend/ has all application code, infra/ only references handler paths' : 'Separación estricta: backend/ tiene todo el código de aplicación, infra/ solo referencia paths de handlers'}
              tradeoff={en ? 'Longer import paths in CDK, but clean architecture. Backend can be tested independently without CDK.' : 'Paths de importación más largos en CDK, pero arquitectura limpia. El backend se puede testear independientemente sin CDK.'}
            />
            <DecisionCard
              title={en ? 'Dual AWS Accounts' : 'Cuentas AWS Duales'}
              context={en ? 'Environment isolation strategy' : 'Estrategia de aislamiento de ambientes'}
              decision={en ? 'Separate AWS accounts for dev and prod with OIDC roles from GitHub' : 'Cuentas AWS separadas para dev y prod con roles OIDC desde GitHub'}
              tradeoff={en ? 'More AWS account management overhead, but account-level blast radius isolation. A mistake in dev cannot affect prod.' : 'Mayor overhead de gestión de cuentas AWS, pero aislamiento a nivel de cuenta. Un error en dev no puede afectar prod.'}
            />
          </div>
        </Section>

        {/* INFRASTRUCTURE AS CODE */}
        <Section id="iac" title={en ? 'Infrastructure as Code' : 'Infraestructura como Código'}>
          <p className="text-text-muted leading-relaxed">
            {en
              ? 'AWS CDK with TypeScript. Nested stacks for modularity. Stage-based configuration (dev/prd) with type-safe config objects. Lambda Powertools for observability (Logger, Metrics, Tracer). NodejsFunction with esbuild bundling and ARM64 architecture for cost optimization.'
              : 'AWS CDK con TypeScript. Nested stacks para modularidad. Configuración basada en stages (dev/prd) con objetos de configuración tipados. Lambda Powertools para observabilidad (Logger, Metrics, Tracer). NodejsFunction con bundling de esbuild y arquitectura ARM64 para optimización de costos.'}
          </p>
          <CodeBlock title={en ? 'CDK Lambda Configuration' : 'Configuración Lambda CDK'}>
            {`const commonLambdaProps = {
  runtime: lambda.Runtime.NODEJS_22_X,     // Latest LTS
  architecture: lambda.Architecture.ARM_64, // ~34% cheaper
  memorySize: 256,                          // MB
  timeout: cdk.Duration.seconds(10),
  tracing: lambda.Tracing.ACTIVE,          // X-Ray
  environment: {
    POWERTOOLS_SERVICE_NAME: 'portfolio',  // Structured logging
    POWERTOOLS_LOG_LEVEL: stage === 'prd' ? 'WARN' : 'DEBUG',
  },
  bundling: {
    minify: stage === 'prd',               // Only in production
    sourceMap: true,                       // Always for debugging
  },
};`}
          </CodeBlock>
          <CodeBlock title={en ? 'Stage Configuration' : 'Configuración por Stage'}>
            {`// infra/lib/config/stage-config.ts
interface StageConfig {
  stage: 'dev' | 'prd';
  aiChat: {
    modelId: string;              // Bedrock model
    maxMessagesPerSession: number;
    maxTokensPerSession: number;
    maxResponseTokens: number;
    rateLimitPerMinute: number;
  };
}

// ${en ? 'Each stage has its own type-safe config:' : 'Cada stage tiene su config tipada:'}
// infra/lib/config/dev.ts → ${en ? 'relaxed limits, DEBUG logging' : 'límites relajados, logging DEBUG'}
// infra/lib/config/prd.ts → ${en ? 'strict limits, WARN logging, minified' : 'límites estrictos, logging WARN, minificado'}`}
          </CodeBlock>
        </Section>

        {/* CLOSING */}
        <section className="rounded border border-accent/30 bg-accent/5 p-8 text-center">
          <p className="font-mono text-sm text-accent">
            {en ? '// Built with' : '// Construido con'} ❤️ {en ? 'and' : 'y'} AI
          </p>
          <p className="mt-4 text-text-muted leading-relaxed">
            {en
              ? 'Every line of code in this project was discussed, evaluated, and written in conversation between a human architect and an AI assistant. The result: a fully serverless, DDD-organized, TDD-tested, CI/CD-deployed portfolio — documented from the inside out.'
              : 'Cada línea de código de este proyecto fue discutida, evaluada y escrita en conversación entre un arquitecto humano y un asistente de IA. El resultado: un portfolio 100% serverless, organizado con DDD, testeado con TDD, desplegado con CI/CD — documentado desde adentro hacia afuera.'}
          </p>
        </section>
      </div>
    </main>
  );
}
